import { useEnterAction } from '../lib/useEnterAction';
import {
  clampMentalImageryRepeats,
  MENTAL_IMAGERY_MAX_REPEATS,
  MENTAL_IMAGERY_MAX_WORD_LENGTH,
  MENTAL_IMAGERY_MIN_REPEATS,
  MENTAL_IMAGERY_WORD_COUNT,
} from '../lib/mentalImagery';

interface MentalImageryIntroProps {
  repeats: number;
  onRepeatsChange: (repeats: number) => void;
  onContinue: () => void;
  onCancel: () => void;
}

export default function MentalImageryIntro({
  repeats,
  onRepeatsChange,
  onContinue,
  onCancel,
}: MentalImageryIntroProps) {
  const safeRepeats = clampMentalImageryRepeats(repeats);
  const totalWrites = MENTAL_IMAGERY_WORD_COUNT * safeRepeats;

  useEnterAction(onContinue);

  return (
    <div className="mx-auto flex w-full max-w-xl flex-col gap-6 animate-fade-in-up text-center">
      <header>
        <p className="text-xs font-medium tracking-[0.2em] uppercase text-stone-400 dark:text-stone-500">
          Ekstra øvelse
        </p>
        <h2 className="mt-3 text-2xl font-light text-strong">
          Imaginær skrivning
        </h2>
        <p className="mt-4 text-sm leading-relaxed text-muted">
          {MENTAL_IMAGERY_WORD_COUNT} korte ord (højst{' '}
          {MENTAL_IMAGERY_MAX_WORD_LENGTH} bogstaver) — tre runder med samme
          ordliste. Ingen tid, ingen point — forestil hele ordet før du taster.
        </p>
      </header>

      <div className="surface-soft rounded-2xl px-5 py-4 text-left">
        <p className="text-sm font-medium text-strong">
          Gentagelser pr. ord
        </p>
        <p className="mt-1 text-xs text-muted">
          Skriv hvert ord {safeRepeats === 1 ? 'én gang' : `${safeRepeats} gange`}{' '}
          i træk, før du går videre — {totalWrites} skrivninger pr. runde.
        </p>
        <input
          type="range"
          min={MENTAL_IMAGERY_MIN_REPEATS}
          max={MENTAL_IMAGERY_MAX_REPEATS}
          step={1}
          value={safeRepeats}
          onChange={(e) =>
            onRepeatsChange(clampMentalImageryRepeats(Number(e.target.value)))
          }
          className="mt-4 w-full accent-stone-600 dark:accent-stone-400"
          aria-label="Antal gentagelser pr. ord"
        />
        <div className="mt-1 flex justify-between text-[10px] text-faint">
          <span>{MENTAL_IMAGERY_MIN_REPEATS}×</span>
          <span className="font-mono text-sm text-strong">{safeRepeats}×</span>
          <span>{MENTAL_IMAGERY_MAX_REPEATS}×</span>
        </div>
      </div>

      <ol className="space-y-3 text-left text-sm text-muted">
        <li className="flex gap-3">
          <span className="mt-0.5 font-mono text-stone-400">1</span>
          <span>
            <strong className="font-medium text-strong">Se og forestil</strong>
            — ordet står stille. Skriv når du er klar; dine bogstaver vises
            undervejs.
          </span>
        </li>
        <li className="flex gap-3">
          <span className="mt-0.5 font-mono text-stone-400">2</span>
          <span>
            <strong className="font-medium text-strong">Kort blink</strong>
            — et sekunds blink, derefter prikker; gentagelser kun prikker.
          </span>
        </li>
        <li className="flex gap-3">
          <span className="mt-0.5 font-mono text-stone-400">3</span>
          <span>
            <strong className="font-medium text-strong">Lyt indeni</strong>
            — samme ord, kun lyd. Hjernen foran fingrene.
          </span>
        </li>
      </ol>

      <div className="flex flex-col gap-2 sm:flex-row">
        <button
          type="button"
          autoFocus
          onClick={onContinue}
          className="flex-1 rounded-full border border-stone-300 bg-stone-50 px-4 py-3 text-sm font-medium text-stone-800 transition hover:bg-stone-100 dark:border-stone-600 dark:bg-stone-800/60 dark:text-stone-100 dark:hover:bg-stone-800"
        >
          Begynd
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 rounded-full px-4 py-3 text-sm font-medium text-muted transition hover:text-strong"
        >
          Tilbage
        </button>
      </div>
      <p className="text-xs text-faint">Enter: begynd</p>
    </div>
  );
}
